<template>
  <div>
    <fuel-consumption-common-dialog
      :accept="select"
      :cancel="close"
      :close="close"
      :showDialog="showDialog"
      :accept-text="acceptText"
      :cancel-text="cancelText"
      :title="title"
    >
      <template v-slot:contents>
        <!-- 年月切り替えスイッチ -->
        <div id="switch-button" class="flex mb-4">
          <div class="switch-item" :class="{ selected: isMonthly }">
            <div class="switch-item-label" @click="clickMonthButton()">
              <div class="flex p-3">
                <span>{{ monthText }}</span>
              </div>
            </div>
          </div>
          <div class="switch-item" :class="{ selected: !isMonthly }">
            <div class="switch-item-label" @click="clickYearButton()">
              <div class="flex p-3">
                <span>{{ yearText }}</span>
              </div>
            </div>
          </div>
        </div>
        <!-- 年選択プルダウン -->
        <div class="mb-4">
          <v-select
            class="w-full"
            v-model="selectedYear"
            :options="yearList"
            :clearable="false"
          />
        </div>
        <!-- 月選択プルダウン -->
        <div class="mb-4">
          <v-select
            class="w-full"
            v-model="selectedMonth"
            v-show="isMonthly"
            :options="monthList"
            :clearable="false"
          />
        </div>
      </template>
    </fuel-consumption-common-dialog>
  </div>
</template>

<script>
import { createNamespacedHelpers } from "vuex";
import { ColorMixin } from "@/mixins";
import SwitchButton from "@/components/buttons/SwitchButton";
import moment from "moment";
import vSelect from "vue-select";
import FuelConsumptionCommonDialog from "./FuelConsumptionCommonDialog.vue";

const { mapActions, mapMutations, mapState } = createNamespacedHelpers(
  "fuelConsumption"
);

export default {
  mixins: [ColorMixin],
  components: {
    vSelect,
    SwitchButton,
    FuelConsumptionCommonDialog,
  },
  data() {
    return {
      error: { message: null },
      showDialog: false,
      title: "月・年次の選択",
      monthText: "月",
      yearText: "年",
      acceptText: "適用",
      cancelText: "キャンセル",
      selectedMonth: null,
      selectedYear: null,
      isMonthly: true,
      startDate: null,
      endDate: null,
      vesselId: null,
      voyageList: [],
    };
  },
  watch: {
    isMonthly(val) {
      if (!val) {
        this.selectedMonth = null;
        this.setDefaultYear();
        return;
      }
      this.setDefaultSelected();
    },
    selectedYear(_) {
      this.setDefaultMonth();
    },
  },
  computed: {
    year() {
      if (!this.selectedYear) return "";
      return this.selectedYear.value;
    },
    month() {
      if (!this.selectedMonth) return "";
      return this.selectedMonth.value;
    },
    yearList() {
      if (this.voyageList.length === 0) return;
      const years = Array.from(new Set(this.voyageList.map(item => moment(item.start_time).format("YYYY")))).sort().reverse();
      return years.map((year, index) => {
        const isThisYear = moment().format("YYYY") === year;
        const label = isThisYear ? `今年（${ year }年）` : `${ year }年`;
        const obj = {
          id: index,
          label: label,
          value: year
        };
        return obj;
      });
    },
    monthList() {
      if (!this.selectedYear) return [];
      if (this.voyageList.length === 0) return [];

      const year = this.selectedYear.value;
      const dates = Array.from(new Set(this.voyageList.map(item => moment(item.start_time).format("YYYYMM"))));
      const months = dates.filter(date => {
        return moment(date).format("YYYY") === year;
      })
      return months.map((date, index) => {
        const label = moment(`${ date }`).format("M月");
        const value = moment(`${ date }`).format("MM");
        return {
          id: index,
          label: label,
          value: value
        };
      }).sort();
    },
  },
  methods: {
    ...mapActions(["fetchAllVoyageList"]),

    select() {

      const routerName = this.isMonthly ? "fuel-consumption-detail-monthly" : "fuel-consumption-detail-yearly";
      const routerParams = {
        vesselId: this.vesselId,
        year: this.year,
      };

      if (this.isMonthly) {
        routerParams.month = this.month;
      }

      this.$router.push({
        name: routerName,
        params: routerParams
      });
      this.close();
    },

    async show(vesselId) {
      try {
        this.vesselId = vesselId;
        await this.getAllVoyageList(vesselId);
        this.initPeriodList();
        this.showDialog = true;
      } catch (error) {
        console.error(error);
        let msg = error.msg;
        if (!msg) {
          msg = "予期せぬエラーが発生しました。";
        }
        this.$vs.notify({
          title: msg,
          color: "danger",
          iconPack: "feather",
          icon: "icon-x-square",
        });
        this.showDialog = false;
        this.vesselId = null;
      }
    },
    close() {
      this.showDialog = false;
      this.vesselId = null;
      this.voyageList = [];
    },

    clickMonthButton() {
      this.isMonthly = true;
    },
    clickYearButton() {
      this.isMonthly = false;
    },
    initPeriodList() {
      this.setDefaultSelected();
    },
    setDefaultSelected() {
      this.setDefaultMonth();
      this.setDefaultYear();
    },

    setDefaultMonth() {
      const nowMoment = moment();
      const currentMonth = this.monthList.find(item => item.value === nowMoment.format("MM"));
      this.selectedMonth = currentMonth ? currentMonth : this.monthList[0];
    },
    setDefaultYear() {
      const nowMoment = moment();
      const currentYear = this.yearList.find(item => item.value === nowMoment.format("YYYY"));
      this.selectedYear = currentYear ? currentYear : this.yearList[0];
    },
    resetPeriods() {
      this.startDate = null;
      this.endDate = null;
    },
    async getAllVoyageList(vesselId) {
      this.voyageList = await this.fetchAllVoyageList(vesselId);
    },

  },
};
</script>

<style lang="scss">
@import "@/assets/scss/aisea/_variables.scss";

#fuel-consumption-common-dialog {
  font-family: $aisea-font-family;
  .vs-dialog {
    width: 350px;
  }

  .vs__dropdown-option--highlight {
    background-color: $aisea-blue;
  }

  #switch-button {
    .switch-item {
      border-top: 1px solid $aisea-blue;
      border-bottom: 1px solid $aisea-blue;
      border-left: 1px solid $aisea-blue;
      color: $aisea-blue;
      width: 50%;
      cursor: pointer;
      line-height: 1;
      opacity: 1;
    }

    .selected {
      background-color: $aisea-blue;
      color: $aisea-white;
      font-weight: 600;
    }

    .switch-item:first-child {
      border-radius: 4px 0px 0px 4px;
    }

    .switch-item:last-child {
      border-right: 1px solid $aisea-blue;
      border-radius: 0px 4px 4px 0px;
    }

    span {
      margin: 0 auto;
    }
  }

  .vs__selected-options {
    padding: 0px 12px;
  }
}
</style>
